import type { ReactNode } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { tableMinWidth } from "./tableMinWidth";
import styles from "./CmsGroupedTable.module.css";

type CmsTableColumn<T> = {
  key: string;
  header?: ReactNode;
  width?: string;
  render: (row: T) => ReactNode;
  className?: string;
};

type CmsTableGroup<T> = {
  name: string;
  count?: number;
  countLabel?: string;
  rows: T[];
  href?: string;
};

type CmsGroupedTableProps<T> = {
  groups: CmsTableGroup<T>[];
  columns: CmsTableColumn<T>[];
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  showHeaders?: boolean;
  defaultCollapsed?: boolean;
  forceExpanded?: boolean;
};

export function CmsGroupedTable<T>({
  groups,
  columns,
  onRowClick,
  rowKey,
  showHeaders = false,
  defaultCollapsed = false,
  forceExpanded = false,
}: CmsGroupedTableProps<T>) {
  const minWidth = tableMinWidth(columns);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    if (!defaultCollapsed) return new Set();
    return new Set(groups.map(g => g.name));
  });

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName);
      else next.add(groupName);
      return next;
    });
  };

  return (
    <div className={styles.groups}>
      {groups.map(group => {
        const isCollapsed = forceExpanded ? false : collapsedGroups.has(group.name);
        // An unnamed group never gets a header bar — whether it's the sole group a one-section
        // list renders, or an unlabelled trailing group of rows no section could be asserted for
        // (see orgSections.ts), a blank-text header bar would be worse than none at all.
        const showGroupHeader = group.name !== "";
        const displayCount = group.count !== undefined ? group.count : group.rows.length;
        const countText = group.countLabel
          ? `${displayCount} ${group.countLabel}`
          : `${displayCount}`;

        return (
          <div key={group.name || "__single__"} className={styles.card}>
            {showGroupHeader && (
              <div className={styles.groupHeader} onClick={() => toggleGroup(group.name)}>
                <span
                  className={styles.groupChevron}
                  style={{
                    transform: isCollapsed ? "rotate(-90deg)" : undefined,
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 4l3 3 3-3"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {group.href ? (
                  <Link
                    to={group.href}
                    className={styles.groupNameLink}
                    onClick={e => e.stopPropagation()}
                  >
                    {group.name}
                  </Link>
                ) : (
                  <span className={styles.groupName}>{group.name}</span>
                )}
                <span className={styles.groupCount}>{countText}</span>
              </div>
            )}
            {!isCollapsed && (
              <div className={styles.tableScroll}>
                <table
                  className={styles.table}
                  style={minWidth === null ? undefined : { minWidth: `${minWidth}px` }}
                >
                  {showHeaders && (
                    <thead>
                      <tr>
                        {columns.map(col => (
                          <th
                            key={col.key}
                            className={col.className}
                            style={col.width ? { width: col.width } : undefined}
                          >
                            {col.header ?? ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {group.rows.map(row => (
                      <tr
                        key={rowKey(row)}
                        className={onRowClick ? styles.clickable : undefined}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                      >
                        {columns.map(col => (
                          <td
                            key={col.key}
                            className={col.className}
                            style={col.width ? { width: col.width } : undefined}
                          >
                            {col.render(row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
